/**
 * Shared completion / "mark finished" policy for annotation UIs (bounding box, ruler, future subtypes).
 * Stored under annotationUI.config.completionPolicy in project config.
 */

/** @typedef {'block' | 'confirm'} WhenBelowStrict */

export const WHEN_BELOW_STRICT = {
    /** Complete button disabled until strict completeness is satisfied. */
    BLOCK: 'block',
    /** Complete button enabled; confirming while incomplete shows a dialog. */
    CONFIRM: 'confirm',
};

/**
 * Machine-readable reasons for analytics / future i18n.
 * TopBarControls may also send human-readable strings in incompleteReasons.
 */
export const INCOMPLETE_REASON = {
    TOOLKIT_NOT_READY: 'toolkit_not_ready',
    SCALE_NOT_RESOLVED: 'scale_not_resolved',
    MISSING_CONFIG: 'missing_config',
    MISSING_CATEGORY_INSTANCE: 'missing_category_instance',
    MISSING_REQUIRED_SLOT: 'missing_required_slot',
    MISSING_ANY_MEASUREMENT: 'missing_any_measurement',
};

/** Default confirmation copy when marking finished below strict completeness (admin can override in config). */
export const DEFAULT_INCOMPLETE_FINISH_MESSAGE =
    'Not all required annotations are in place for this item. Are you sure you want to mark it as finished?';

/**
 * @param {object} [uiConfig] - annotationUI.config
 * @returns {{ whenBelowStrict: WhenBelowStrict, incompleteFinishMessage: string }}
 */
export function normalizeCompletionPolicy(uiConfig) {
    const raw = uiConfig?.completionPolicy;
    const whenBelowRaw = raw?.whenBelowStrict;
    const whenBelowStrict =
        whenBelowRaw === WHEN_BELOW_STRICT.CONFIRM ? WHEN_BELOW_STRICT.CONFIRM : WHEN_BELOW_STRICT.BLOCK;
    const custom = typeof raw?.incompleteFinishMessage === 'string' ? raw.incompleteFinishMessage.trim() : '';
    const incompleteFinishMessage = custom || DEFAULT_INCOMPLETE_FINISH_MESSAGE;
    return { whenBelowStrict, incompleteFinishMessage };
}

/**
 * Payload reported by each UI's TopBarControls to ProjectViewer / ViewerTopBar.
 * @typedef {object} AnnotationCompletionStatus
 * @property {boolean} completable - Whether the "Item finished" control should be enabled (when not already completed).
 * @property {boolean} strictComplete - Whether this UI's strict completeness bar is met.
 * @property {string[]} incompleteReasons - Human-readable hints (e.g. for disabled tooltip).
 * @property {string[]} incompleteReasonCodes - Values from INCOMPLETE_REASON where applicable.
 */
